import type { ComponentProps } from 'react';
import { useCallback } from 'react';
import { useTracking } from 'react-tracking';

import { Sentry } from '@/components/Sentry';
import { RouterLink } from '@/utilities/redux-first-router.export';
import { useToggleStateOnMatchQueryParam } from '@/hooks/useToggleStateOnMatchQueryParam';
import { useQueryStringParam } from '@/hooks/useQueryStringParam';

import type { WatchlistItem } from '@/hooks/useWatchlist';
import type { CurrencyISO } from '@/constants/currencies';

import { PopulatedState } from './PopulatedState';
import { DefaultState } from './DefaultState';
import { EmptyState } from './EmptyState';
import { type Holding } from '@/hooks/portfolio/usePortfolio';
import { selectLocationState } from 'redux-first-router';
import { useSelector } from 'react-redux';
import { FairValueModalWithSentry } from '../FairValueModalWithSentry';

export type PrimaryNarrativeProps = {
  companyName: string;
  tickerSymbol: string;
  companyId: string;
  currencyISO: CurrencyISO;
  myValuation:
    | WatchlistItem['company']['myValuation']
    | Holding['company']['myValuation'];
  defaultNarrative: ComponentProps<typeof DefaultState>['defaultNarrative'];
  isOnboardingNarrative?: boolean;
};

type PrimaryRenderProps = Pick<
  PrimaryNarrativeProps,
  'myValuation' | 'companyName' | 'currencyISO' | 'tickerSymbol'
> & {
  defaultNarrative: PrimaryNarrativeProps['defaultNarrative'] | undefined;
};

const PrimaryNarrativeRender = ({
  myValuation,
  companyName,
  currencyISO,
  defaultNarrative,
  tickerSymbol,
}: PrimaryRenderProps) => {
  return (
    <>
      {myValuation && (
        <PopulatedState
          companyName={companyName}
          primaryNarrative={myValuation}
          currencyISO={currencyISO}
        />
      )}
      {!myValuation && defaultNarrative && (
        <DefaultState
          defaultNarrative={defaultNarrative}
          currencyISO={currencyISO}
        />
      )}
      {!myValuation && !defaultNarrative && (
        <EmptyState tickerSymbol={tickerSymbol} />
      )}
    </>
  );
};

export const PrimaryNarrative = ({
  companyName,
  tickerSymbol,
  companyId,
  currencyISO,
  myValuation,
  defaultNarrative,
  isOnboardingNarrative,
}: PrimaryNarrativeProps) => {
  const { trackEvent } = useTracking({ modifier: 'primary_narrative' });
  const { setItem, getItem, removeItem } = useQueryStringParam();
  const {
    isToggleOn: isModalOpen,
    handleToggleOff,
    handleToggleOn,
  } = useToggleStateOnMatchQueryParam({
    matchesQueryParam: () =>
      getItem('company')?.toLowerCase() === companyId.toLowerCase(),
    setQueryParam: setItem,
    removeQueryParam: removeItem,
    queryParam: { key: 'company', value: companyId.toLowerCase() },
  });

  const location = useSelector(selectLocationState);
  const pathSegments = location.pathname.split('/').filter(Boolean);

  const handleRouterLinkClick = useCallback(() => {
    handleToggleOn();
    trackEvent({
      action: myValuation ? 'edit' : 'select',
    });
  }, [trackEvent, handleToggleOn, myValuation]);

  return (
    <Sentry sentryCapture={{ team: 'narwhals' }} globalDisabled>
      <RouterLink
        className="no-underline"
        to={`/${pathSegments.join('/')}?company=${companyId}`}
        onClick={handleRouterLinkClick}
      >
        <>
          {myValuation && (
            <PopulatedState
              companyName={companyName}
              primaryNarrative={myValuation}
              currencyISO={currencyISO}
            />
          )}
          {!myValuation && defaultNarrative && (
            <DefaultState
              defaultNarrative={defaultNarrative}
              currencyISO={currencyISO}
              isOnboardingNarrative={isOnboardingNarrative}
            />
          )}
          {!myValuation && !defaultNarrative && (
            <EmptyState tickerSymbol={tickerSymbol} />
          )}
        </>
      </RouterLink>
      <FairValueModalWithSentry
        isOpen={isModalOpen}
        onClose={handleToggleOff}
        companyId={companyId}
      />
    </Sentry>
  );
};

export const DemoPrimaryNarrative = ({
  companyName,
  tickerSymbol,
  currencyISO,
  myValuation,
  defaultNarrative,
}: PrimaryNarrativeProps) => {
  return (
    <Sentry sentryCapture={{ team: 'narwhals' }} globalDisabled>
      <PrimaryNarrativeRender
        companyName={companyName}
        tickerSymbol={tickerSymbol}
        currencyISO={currencyISO}
        myValuation={myValuation}
        defaultNarrative={defaultNarrative}
      />
    </Sentry>
  );
};
